import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { Compass, FlaskConical, FolderKanban, Menu, Users } from 'lucide-react';
import { Button, Sidebar, SkipLink } from '@ludolio/shared-ui';
import { DemoBanner } from '@/components/DemoBanner';
import { useLabs } from '@/lib/labs-context';
import { useAuthStore } from '@/stores/authStore';
import { useDemoStore } from '@/stores/demoStore';
import { useThemeStore } from '@/stores/themeStore';

export function Layout({ children }: { children: ReactNode }) {
  const labs = useLabs();
  const navigate = useNavigate();
  const logout = useAuthStore((state) => state.logout);
  const isDarkMode = useThemeStore((state) => state.isDarkMode);
  const toggleDarkMode = useThemeStore((state) => state.toggleDarkMode);
  const currentPath = useRouterState({ select: (state) => state.location.pathname });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const base = `/${labs.organization.slug}`;
  const navItems = [
    { label: 'Projects', href: `${base}/projects`, icon: FolderKanban },
    { label: 'Teams', href: `${base}/teams`, icon: Users },
    { label: 'Discover', href: `${base}/discover`, icon: Compass },
  ];
  const activePath = navItems.find((item) => currentPath.startsWith(item.href))?.href ?? currentPath;

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [currentPath]);

  const pageLabel = navItems.find((item) => item.href === activePath)?.label ?? 'Labs';
  useEffect(() => {
    document.title = `${pageLabel} — Ludolio Labs`;
  }, [pageLabel]);

  const handleLogout = async () => {
    const demo = useDemoStore.getState().isDemoMode;
    await logout();
    if (demo) {
      navigate({ to: '/login', search: { returnTo: '/' }, replace: true });
      return;
    }
    navigate({ to: '/login', search: { returnTo: currentPath } });
  };

  const logo = (
    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[hsl(var(--color-primary))]">
      <FlaskConical className="h-5 w-5 text-[hsl(var(--color-primary-foreground))]" />
    </div>
  );

  const sidebar = (id?: string) => (
    <Sidebar
      id={id}
      logo={logo}
      title="Labs"
      navItems={navItems}
      user={{ name: labs.user.name, email: labs.user.email }}
      activePath={activePath}
      isDarkMode={isDarkMode}
      onToggleDarkMode={toggleDarkMode}
      onLogout={handleLogout}
      LinkComponent={Link}
    />
  );

  return (
    <div className="flex h-screen flex-col bg-[hsl(var(--color-background))]">
      <SkipLink />
      <DemoBanner />
      <div className="flex min-h-0 flex-1">
        <div className="hidden lg:block">{sidebar()}</div>

        {mobileMenuOpen && (
          <>
            <div className="fixed inset-y-0 left-0 z-40 lg:hidden" role="dialog" aria-modal="true" aria-label="Navigation menu">
              {sidebar('labs-mobile-navigation')}
            </div>
            <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" aria-hidden="true" onClick={() => setMobileMenuOpen(false)} />
          </>
        )}

        <main id="main-content" className="w-full flex-1 overflow-auto">
          <div className="sticky top-0 z-20 flex items-center gap-3 border-b border-border bg-[hsl(var(--color-card))] px-4 py-3 lg:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen((open) => !open)}
              aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={mobileMenuOpen}
              aria-controls="labs-mobile-navigation"
            >
              <Menu />
            </Button>
            <span className="font-semibold">{labs.organization.name}</span>
          </div>
          <div className="container mx-auto max-w-6xl p-4 sm:p-6">{children}</div>
        </main>
      </div>
    </div>
  );
}
